import { useState } from "react";
import type { StreamData } from "./api";
import type { TreeNode } from "./tree";
import { fmtBytes, fmtDuration } from "./tree";
import { useDashboardStore, type StreamStats } from "./store";

type SortKey = "stage" | "day" | "type" | "status" | "pct" | "size" | "duration" | "stored" | "pending";
type SortDir = "asc" | "desc";

function stTotal(s: StreamStats | undefined): number {
    if (!s) return 0;
    return s.pending + s.downloading + s.stored + s.failed + s.expired;
}

/** Extract display metadata from a stream for table/grid rendering */
function displayMeta(s: StreamData) {
    const title = s.title || s.stream_key;
    const dayMatch = title.match(/Day\s*(\d+)/i);
    const day = dayMatch ? `Day ${dayMatch[1]}` : "";
    const stageMatch = title.match(/Day\s*\d+\s*[-–]\s*(.+)/i);
    const stage = stageMatch ? stageMatch[1].trim() : title;
    const type = s.type || (s.stream_key.startsWith("dvr-") ? "dvr" : "live");
    return { day, stage, type };
}

function StatsRow({ stream: s, onSelect }: { stream: StreamData; onSelect: () => void }) {
    const meta = displayMeta(s);
    const vs = useDashboardStore(st => st.stats[s.stream_key]);
    const as = useDashboardStore(st => st.stats[s.stream_key + "-audio"]);

    const storedN = vs?.stored || 0;
    const total = stTotal(vs);
    const pendingN = (vs?.pending || 0) + (vs?.downloading || 0);
    const aStored = as?.stored || 0;
    const aTotal = stTotal(as);
    const combinedStored = storedN + aStored;
    const combinedTotal = total + aTotal;
    const pct = combinedTotal > 0 ? Math.round(combinedStored / combinedTotal * 100) : 0;
    const size = vs?.totalSize || 0;
    const duration = vs?.totalDuration || 0;

    return (
        <tr onClick={onSelect} style={{ cursor: "pointer" }}
            data-pct={pct} data-size={size} data-duration={duration}
            data-stored={storedN} data-pending={pendingN}
            data-day={meta.day} data-stage={meta.stage} data-type={meta.type}>
            <td>{meta.day}</td>
            <td>
                {s.status === "active" && <span className="live-dot" style={{ width: 6, height: 6 }} />}
                {meta.stage}
                {s.event_id && <span style={{ color: "#444", fontSize: "0.8em", marginLeft: 4 }}>#{s.event_id}</span>}
            </td>
            <td><span className={`badge-sm ${meta.type}`}>{meta.type}</span></td>
            <td><span className={`badge-sm ${s.status}`}>{s.status}</span></td>
            <td>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div className="mini-bar">
                        <div className="pb-stored" style={{ width: `${pct}%` }} />
                    </div>
                    <span style={{ color: pct >= 99 ? "#33aa55" : pct > 50 ? "#aa8833" : "#aa3333", fontVariantNumeric: "tabular-nums" }}>
                        {pct}%
                    </span>
                </div>
            </td>
            <td style={{ fontVariantNumeric: "tabular-nums" }}>{storedN.toLocaleString()}</td>
            <td style={{ fontVariantNumeric: "tabular-nums", color: pendingN > 0 ? "#aa8833" : "#555" }}>
                {pendingN > 0 ? pendingN.toLocaleString() : "-"}
            </td>
            <td style={{ fontVariantNumeric: "tabular-nums" }}>{fmtBytes(size)}</td>
            <td style={{ fontVariantNumeric: "tabular-nums" }}>{fmtDuration(duration)}</td>
        </tr>
    );
}

export function GridView({ node, onSelectStream }: { node: TreeNode; onSelectStream: (key: string) => void }) {
    const streams = node.streams || [];
    const [sortKey, setSortKey] = useState<SortKey>("day");
    const [sortDir, setSortDir] = useState<SortDir>("asc");
    const [viewMode, setViewMode] = useState<"grid" | "table">("table");

    if (streams.length === 0) return <div className="empty">no videos</div>;

    const toggleSort = (key: SortKey) => {
        if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
        else { setSortKey(key); setSortDir("asc"); }
    };

    const sorted = [...streams].sort((a, b) => {
        const am = displayMeta(a);
        const bm = displayMeta(b);
        let cmp = 0;
        switch (sortKey) {
            case "stage": cmp = am.stage.localeCompare(bm.stage); break;
            case "day": {
                const da = parseInt(am.day.replace(/\D/g, "")) || 0;
                const db = parseInt(bm.day.replace(/\D/g, "")) || 0;
                cmp = da - db || am.stage.localeCompare(bm.stage);
                break;
            }
            case "type": cmp = am.type.localeCompare(bm.type); break;
            case "status": cmp = a.status.localeCompare(b.status); break;
            default: {
                const da = parseInt(am.day.replace(/\D/g, "")) || 0;
                const db = parseInt(bm.day.replace(/\D/g, "")) || 0;
                cmp = da - db || am.stage.localeCompare(bm.stage);
            }
        }
        return sortDir === "desc" ? -cmp : cmp;
    });

    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h2 style={{ margin: 0 }}>{node.label}</h2>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ fontSize: "0.82em", color: "#666" }}>{streams.length} videos</span>
                    <button className="btn btn-sm" style={{ background: viewMode === "table" ? "#da4165" : "#333" }}
                        onClick={() => setViewMode("table")}>table</button>
                    <button className="btn btn-sm" style={{ background: viewMode === "grid" ? "#da4165" : "#333" }}
                        onClick={() => setViewMode("grid")}>grid</button>
                </div>
            </div>

            {viewMode === "grid" ? (
                <div className="thumb-grid">
                    {sorted.map(s => (
                        <ThumbCard key={s.stream_key} stream={s} onClick={() => onSelectStream(s.stream_key)} />
                    ))}
                </div>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table className="data-table">
                        <thead>
                            <tr>
                                <Th k="day" current={sortKey} dir={sortDir} onClick={toggleSort}>Day</Th>
                                <Th k="stage" current={sortKey} dir={sortDir} onClick={toggleSort}>Stage</Th>
                                <Th k="type" current={sortKey} dir={sortDir} onClick={toggleSort}>Type</Th>
                                <Th k="status" current={sortKey} dir={sortDir} onClick={toggleSort}>Status</Th>
                                <Th k="pct" current={sortKey} dir={sortDir} onClick={toggleSort}>Complete</Th>
                                <Th k="stored" current={sortKey} dir={sortDir} onClick={toggleSort}>Stored</Th>
                                <Th k="pending" current={sortKey} dir={sortDir} onClick={toggleSort}>Pending</Th>
                                <Th k="size" current={sortKey} dir={sortDir} onClick={toggleSort}>Size</Th>
                                <Th k="duration" current={sortKey} dir={sortDir} onClick={toggleSort}>Duration</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map(s => (
                                <StatsRow key={s.stream_key} stream={s} onSelect={() => onSelectStream(s.stream_key)} />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function Th({ k, current, dir, onClick, children }: {
    k: SortKey; current: SortKey; dir: SortDir; onClick: (k: SortKey) => void; children: React.ReactNode;
}) {
    const active = current === k;
    return (
        <th onClick={() => onClick(k)} style={{ cursor: "pointer", userSelect: "none" }}>
            {children} {active ? (dir === "asc" ? "▲" : "▼") : <span style={{ color: "#333" }}>▲</span>}
        </th>
    );
}

function ThumbCard({ stream: s, onClick }: { stream: StreamData; onClick: () => void }) {
    const meta = displayMeta(s);
    const isActive = s.status === "active";
    const vs = useDashboardStore(st => st.stats[s.stream_key]);
    const as = useDashboardStore(st => st.stats[s.stream_key + "-audio"]);
    const storedN = vs?.stored || 0;
    const total = stTotal(vs);
    const aStored = as?.stored || 0;
    const aTotal = stTotal(as);
    const pct = (total + aTotal) > 0 ? Math.round((storedN + aStored) / (total + aTotal) * 100) : 0;
    const size = vs?.totalSize || 0;
    const duration = vs?.totalDuration || 0;

    return (
        <div className="grid-card" onClick={onClick}>
            <div className="grid-thumb">
                {s.thumb_s3_key ? (
                    <img src={`/api/thumb/${encodeURIComponent(s.stream_key)}`} loading="lazy" />
                ) : (
                    <div className="grid-thumb-placeholder">{meta.stage.charAt(0)}</div>
                )}
                <div className="grid-pct" style={{ background: pct >= 99 ? "#33aa55" : pct > 0 ? "#aa8833" : "#555" }}>
                    {pct}%
                </div>
                {isActive && <div className="grid-live">LIVE</div>}
            </div>
            <div className="grid-info">
                <div className="grid-title">{meta.day} - {meta.stage}</div>
                <div className="grid-meta">{fmtBytes(size)} · {fmtDuration(duration)}</div>
            </div>
            <div className="progress-bar" style={{ height: 3 }}>
                <div className="pb-stored" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}
